import {
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const pillars = [
  {
    id: 'wellness',
    icon: ClipboardDocumentCheckIcon,
    title: 'Wellness That Reflects Real Life',
    subtitle:
      'Understand how someone is truly doing, every day.',
    description:
      'SeniorThrive pays attention to everyday patterns and routines, without turning daily life into a medical process.',
    included: [
      'MyDailyCheckIn for simple, daily reflection',
      'ThriveScore that turns patterns into clear, usable insight',
      'Insight into daily living tasks and routines',
      'Trends in mood, movement, and consistency over time',
      'Gentle prompts that encourage healthy habits',
    ],
    tagline: 'This builds clarity and confidence.',
    accentColor: 'color-green',
  },
  {
    id: 'safety',
    icon: ShieldCheckIcon,
    title: 'Home Safety That Prevents Problems',
    subtitle: 'Most risks live right where daily life happens.',
    description:
      'SeniorThrive helps identify and reduce safety risks room by room, with guidance that is practical and easy to act on.',
    included: [
      'Home Safety Scan, powered by ThriveVision',
      'Visual identification of common home hazards',
      'Personalized safety recommendations',
      'DIY fixes, product suggestions, and local professional referrals',
      'Progress tracking as improvements are made',
    ],
    tagline: "You don't just see risk. You know what to do next.",
    accentColor: 'primary',
  },
  {
    id: 'connection',
    icon: UserGroupIcon,
    title: 'Connection That Keeps Everyone Aligned',
    subtitle: 'Independence works best when no one is guessing.',
    description:
      'SeniorThrive keeps older adults, family members, and caregivers connected through a shared picture, with clear boundaries and full respect for privacy.',
    included: [
      'ThriveCircle shared dashboard',
      'Role-based access for older adults, family, and caregivers',
      'Notes, updates, and shared context',
      'Alerts and reminders that actually matter',
      'Fewer texts, fewer misunderstandings, more clarity',
    ],
    tagline: 'Support works around you, on your terms.',
    accentColor: 'color-purple',
  },
];

const CorePillarsSection = () => {
  return (
    <div className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-4">
          Wellness, Safety, and Support,{' '}
          <span className="text-primary">Working Together</span>
        </h2>
        <p className="text-lg text-muted-foreground text-center mb-16 max-w-2xl mx-auto">
          SeniorThrive is built around three pillars that reinforce each other
          every day.
        </p>

        {/* Shared container to reinforce interdependence */}
        <div className="relative max-w-6xl mx-auto">
          {/* Background connector */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/5 via-transparent to-primary/5 -m-4 lg:-m-6"></div>
          {/* Horizontal connecting line (desktop) */}
          <div className="hidden lg:block absolute top-1/2 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>

          <div className="relative grid lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <div
                key={pillar.id}
                className="group relative p-8 bg-background rounded-2xl border border-border shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 flex flex-col"
              >
                {/* Pillar number */}
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                  {index + 1}
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                    <pillar.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {pillar.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-1 italic">
                  {pillar.subtitle}
                </p>
                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  {pillar.description}
                </p>

                <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                  Included:
                </p>
                <ul className="space-y-2 mb-6">
                  {pillar.included.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircleIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-border pt-4 mt-auto">
                  <p className="text-primary font-semibold text-sm">
                    {pillar.tagline}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorePillarsSection;
